import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import FooterTailwind from 'TailwindcssComponents/Footer'
import TailwindLayout from 'TailwindcssComponents/TailwindLayout'
import TopBar from 'TailwindcssComponents/TopBar'
import QRCode from 'qrcode';
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import defaultcover from 'assets/images/logos/defaultcover.png'
import defaultcoverWhite from 'assets/images/logos/defaultCoverWhite.png'

export default function DetailedTicketPageByRegistration() {
    const { id, eventId } = useParams();
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const targetRef = useRef();
    const [isLoading, setIsLoading] = useState(true);
    const [ticketsData, setTicketsData] = useState([]);
    const [registrationData, setRegistrationData] = useState({});
    const [eventData, setEventData] = useState({});
    const [selectedTicketIndex, setSelectedTicketIndex] = useState(0);
    const [paymentData, setPaymentData] = useState({});
    const [formFieldData, setFormFieldData] = useState([]);
    const ticketRefs = useRef([]);
    const navigate = useNavigate();

    const generateQRCodes = async (referenceId, registrationId, eventId, ticketId) => {
        try {
            return await QRCode.toDataURL(
                `ReferenceID:${referenceId} && RegistrationID:${registrationId} && EventID:${eventId} && TicketID:${ticketId}`
            );
        } catch (err) {
            console.error("Error generating QR code:", err);
            return null;
        }
    };

    const fetchRegistration = async () => {
        try {
            setIsLoading(true);

            const [eventResponse, ticketResponse, registrationResponse, paymentResponse, zonesResponse] = await Promise.all([
                axios.get(`${apiUrl}event-service/event/${eventId}`),
                axios.get(`${apiUrl}event-service/event-ticket`, { params: { event: eventId, registration: id } }),
                axios.get(`${apiUrl}event-service/event-registration/${id}`),
                axios.get(`${apiUrl}event-service/event-payment`, { params: { event: eventId, registration: id } }),
                axios.get(`${apiUrl}event-service/event-zone`, { params: { event: eventId } }),
            ]);

            if (!eventResponse.data?.success || !ticketResponse.data?.success || !registrationResponse.data?.success || !paymentResponse.data?.success || !zonesResponse.data?.success) {
                throw new Error("Failed to fetch event or tickets.");
            }

            const event = eventResponse.data.data;
            const registrations = registrationResponse.data.data;
            const tickets = ticketResponse.data.data;
            const payments = paymentResponse.data.data[0];
            const zones = zonesResponse.data.data;
            // Generate QR codes for each ticket
            const ticketsWithQr = await Promise.all(
                tickets.map(async (ticket) => {
                    const zone = zones.find(zone => zone.id === ticket?.ticketCategory?.zoneId) || {};
                    const seat = zone?.seats?.find(seat => seat.id === ticket.seatId) || null;
                    return {
                        ...ticket,
                        qrCode: await generateQRCodes(ticket.referenceId, id, eventId, ticket.id),
                        event,
                        zone,
                        seat,
                    };
                }),
            );
            setEventData(event);
            setTicketsData(ticketsWithQr);
            setRegistrationData(registrations);
            setPaymentData(payments);
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchRegistrationFormField = async () => {
        try {
            const res = await axios.get(`${apiUrl}event-service/registration-form`, {
                params: { event: eventId },
            });
            if (res.data?.success) setFormFieldData(res.data.data);
        } catch (error) {
            console.error("Error fetching form fields:", error);
        }
    };

    useEffect(() => {
        fetchRegistration();
        fetchRegistrationFormField();
    }, [id]);

    const formattedDateTime = (date) => {
        return new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    };

    const formattedTime = (time) => {
        const [hours, minutes, seconds] = time.split(':');
        const date = new Date();
        date.setHours(hours, minutes, seconds);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
    };

    const selectedTicket = ticketsData[selectedTicketIndex];

    const downloadAllTickets = async () => {
        if (!ticketsData.length || !targetRef.current) return;

        try {
            // Save the original index to restore it later
            const originalIndex = selectedTicketIndex;

            for (let index = 0; index < ticketsData.length; index++) {
                // Update the selected ticket index to render the correct ticket
                setSelectedTicketIndex(index);

                // Wait for the component to re-render and images to load
                await new Promise(resolve => setTimeout(resolve, 1000));

                try {
                    // Use the ref directly instead of looking for an ID
                    if (targetRef.current) {
                        // Configure html2canvas to properly handle images
                        const canvas = await html2canvas(targetRef.current, {
                            useCORS: true,          // Enable CORS for images
                            allowTaint: true,       // Allow cross-origin images
                            logging: true,          // Enable logging for debugging
                            imageTimeout: 5000,     // Wait up to 5 seconds for images to load
                        });

                        const imgData = canvas.toDataURL("image/png");

                        // Create PDF with A7 dimensions (74mm x 105mm)
                        const pdf = new jsPDF({
                            orientation: "portrait",
                            unit: "mm",
                            format: [74, 105]  // A7 size
                        });

                        const ticket = ticketsData[index];
                        const filename = `${ticket.event?.name}_${ticket.registration?.name || index + 1}.pdf`;

                        // Calculate dimensions to fit the image within A7 paper size
                        // Leave 5mm margins on all sides
                        const pageWidth = 74;
                        const pageHeight = 105;
                        const margin = 5;
                        const imgWidth = pageWidth - (margin * 2);
                        const imgHeight = (canvas.height * imgWidth) / canvas.width;

                        // If the image height exceeds available space, scale it down
                        const availableHeight = pageHeight - (margin * 2);
                        const scaleFactor = imgHeight > availableHeight ? availableHeight / imgHeight : 1;
                        const finalWidth = imgWidth * scaleFactor;
                        const finalHeight = imgHeight * scaleFactor;

                        // Center the image horizontally
                        const xPos = (pageWidth - finalWidth) / 2;

                        // Add the image to the PDF
                        pdf.addImage(imgData, 'PNG', xPos, margin, finalWidth, finalHeight);

                        pdf.save(filename);
                    } else {
                        console.error("Target ref is null during PDF generation for ticket", index);
                    }
                } catch (err) {
                    console.error(`Error generating PDF for ticket ${index + 1}:`, err);
                }
            }

            // Restore the original selected ticket
            setSelectedTicketIndex(originalIndex);

        } catch (error) {
            console.error("Error in download process:", error);
        }
    };

    const waitForImageLoad = (imgElement) => {
        return new Promise((resolve) => {
            if (imgElement.complete) {
                resolve();
            } else {
                imgElement.onload = resolve;
                imgElement.onerror = resolve; // Still resolve even if image fails
            }
        });
    };

    const downloadAllTicketsAsSinglePDF = async () => {
        if (!ticketsData.length || !ticketRefs.current.length) return;

        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: [74, 105] // A7 size
        });
        for (let i = 0; i < ticketsData.length; i++) {
            const node = ticketRefs.current[i];
            if (!node) {
                console.warn(`Ticket DOM node missing for index ${i}`);
                continue;
            }

            try {
                const imgElements = node.querySelectorAll("img");
                for (const img of imgElements) {
                    await waitForImageLoad(img);
                }
                const canvas = await html2canvas(node, {
                    useCORS: true,
                    allowTaint: false,
                    imageTimeout: 5000,
                    scrollY: -window.scrollY, // Fix viewport offset issues
                    scale: 2, // Increases image resolution
                });


                const imgData = canvas.toDataURL("image/png");

                const pageWidth = 74;
                const pageHeight = 105;
                const margin = 5;
                const imgWidth = pageWidth - (margin * 2);
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                const availableHeight = pageHeight - (margin * 2);
                const scaleFactor = imgHeight > availableHeight ? availableHeight / imgHeight : 1;
                const finalWidth = imgWidth * scaleFactor;
                const finalHeight = imgHeight * scaleFactor;
                const xPos = (pageWidth - finalWidth) / 2;

                if (i > 0) {
                    pdf.addPage([74, 105]);
                }

                pdf.addImage(imgData, 'PNG', xPos, margin, finalWidth, finalHeight);
            } catch (error) {
                console.error(`Error rendering ticket at index ${i}:`, error);
            }
        }
        pdf.save(`${ticketsData[0].event?.name}_${ticketsData[0].registration?.name}.pdf`);
    };

    return (
        <TailwindLayout>
            <TopBar />
            <div className="p-12 bg-gray-100 min-h-screen mb-8">
                {isLoading ? (
                    <div className="p-4 bg-white font-sans animate-pulse space-y-6">
                        {/* Skeleton Header */}
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                            <div className="h-6 bg-gray-300 rounded w-2/3"></div>
                            <div className="space-x-2 flex">
                                <div className="h-8 bg-gray-300 rounded w-32"></div>
                                <div className="h-8 bg-gray-300 rounded w-32"></div>
                            </div>
                        </div>

                        {/* Skeleton Main Content */}
                        <div className="flex flex-col md:flex-row gap-12 ml-12 mr-12">
                            {/* Left Skeleton */}
                            <div className="md:w-3/4 bg-gray-100 rounded-md p-6 space-y-4">
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <div key={i} className="h-4 bg-gray-300 rounded w-full"></div>
                                ))}
                                <div className="flex justify-end">
                                    <div className="h-8 w-40 bg-gray-300 rounded"></div>
                                </div>
                            </div>

                            {/* Right Skeleton - Ticket */}
                            <div className="w-[380px] space-y-4">
                                <div className="w-full h-48 bg-gray-300 rounded-lg"></div>
                                <div className="w-36 h-36 bg-gray-300 rounded mx-auto"></div>
                                <div className="h-6 bg-gray-300 rounded w-3/4 mx-auto"></div>
                                <div className="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>

                                <div className="grid grid-cols-3 gap-4 mt-6">
                                    {Array.from({ length: 3 }).map((_, i) => (
                                        <div key={i} className="space-y-2">
                                            <div className="h-4 bg-gray-300 rounded w-full"></div>
                                            <div className="h-4 bg-gray-300 rounded w-3/4 mx-auto"></div>
                                        </div>
                                    ))}
                                </div>

                                <div className="h-10 bg-gray-300 rounded w-1/3 mx-auto mt-6"></div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="p-4 bg-white font-sans">
                        {/* Header with Event Name and Download Button */}
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                            <h1 className="text-xl font-semibold">
                                {eventData?.name} : {selectedTicket?.referenceId || "No reference id"} : {registrationData?.name}
                            </h1>
                            <div>
                                {ticketsData && ticketsData.length > 1 && (
                                    <button
                                        onClick={downloadAllTickets}
                                        className="bg-gray-200 hover:bg-gray-300 text-black py-2 px-4 rounded mr-1 mb-1">
                                        Download Each Ticket
                                    </button>
                                )}
                                <button
                                    onClick={downloadAllTicketsAsSinglePDF}
                                    className="bg-gray-200 hover:bg-gray-300 text-black py-2 px-4 rounded">
                                    Download All Tickets
                                </button>
                            </div>
                        </div>

                        {/* Ticket View */}
                        <div className="flex justify-between flex-col md:flex-row mt-6 gap-12 ml-12 mr-12">
                            {/* Left Side - Ticket Information */}
                            <div className="md:w-3/4 bg-white rounded-md shadow-md p-6">
                                <div className="mb-4 border-b flex flex-row items-stretch">
                                    <div className="flex-1">
                                        <p className="text-xs text-gray-500">Booked on</p>
                                        <p className="font-medium mb-2">{formattedDateTime(selectedTicket?.createdAt)}</p>
                                    </div>
                                    <div className="border-l mb-2 mr-4"></div>
                                    <div className="flex-1">
                                        <p className="text-xs text-gray-500">Mode Of Payment</p>
                                        <p className="font-medium mb-2">{paymentData?.status === 'manual' ? 'Manual' : 'Online'}</p>
                                    </div>
                                </div>

                                <div className="mb-4 border-b flex flex-row items-stretch">
                                    <div className="flex-1">
                                        <p className="text-xs text-gray-500">No. of Tickets</p>
                                        <p className="font-medium mb-2">{ticketsData.length}</p>
                                    </div>
                                    <div className="border-l mb-2 mr-4"></div>
                                    <div className="flex-1">
                                        <p className="text-xs text-gray-500">Reference ID</p>
                                        <p className="font-medium mb-2">{selectedTicket?.referenceId || "No reference id"}</p>
                                    </div>
                                </div>

                                <div className="mb-4 border-b">
                                    <p className="text-xs text-gray-500">Name</p>
                                    <p className="font-medium mb-2">{registrationData?.name}</p>
                                </div>

                                <div className="mb-4 border-b">
                                    <p className="text-xs text-gray-500">Email</p>
                                    <p className="font-medium mb-2">{registrationData?.email}</p>
                                </div>

                                <div className="mb-4 border-b">
                                    <p className="text-xs text-gray-500">Phone</p>
                                    <p className="font-medium mb-2">{registrationData?.phone}</p>
                                </div>
                                {registrationData?.details && (
                                    Object.entries(registrationData.details).map(([field, value]) => (
                                        <div key={field} className="mb-4 border-b">
                                            <p className="text-xs text-gray-500">{field}</p>
                                            <p className="font-medium mb-2">{String(value)}</p>
                                        </div>
                                    ))
                                )}
                                <div className='mb-4 flex justify-end'>
                                    <button
                                        onClick={() => navigate(`/view-eventTickets/detailedView/editRegistration/${id}`, { state: { registrationData, formFieldData } })}
                                        className="bg-gray-200 hover:bg-gray-300 text-black py-2 px-4 rounded">
                                        Edit Registration Data
                                    </button>
                                </div>
                            </div>

                            {/* Right Side - Selected Ticket */}
                            <div className="md:w-380px">
                                <div ref={targetRef} className="w-[380px] rounded-xl bg-white shadow-[0_-2px_10px_0_rgba(0,0,0,0.1)]">
                                    {/* Event image */}
                                    <div className="pt-4 px-4">
                                        <div className="h-48 rounded-lg overflow-hidden">
                                            <img
                                                src={eventData?.coverImage?.secure_url || defaultcover}
                                                alt="Event background"
                                                className="object-cover w-full h-full"
                                            />
                                        </div>
                                    </div>

                                    <div className="p-2 flex flex-col items-center">
                                        {/* QR Code */}
                                        <div className="bg-white -mt-20 rounded-lg mb-4 p-2 shadow-md">
                                            <div className="w-36 h-36 bg-white flex items-center justify-center border border-gray-200">
                                                <div className="text-center">
                                                    {selectedTicket?.qrCode && (
                                                        <img
                                                            src={selectedTicket.qrCode}
                                                            alt="QR Code"
                                                            className="h-32 w-32"
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <p className="text-xs text-center mt-1 text-gray-500">{selectedTicket.referenceId}</p>
                                        </div>

                                        {/* Event Name */}
                                        <h2 className="text-2xl font-bold text-center mb-1">{eventData?.name || "N/A"}</h2>
                                        <div className="flex items-center text-sm text-gray-600 mb-6">
                                            <span className="text-lg mr-1">📍</span>
                                            <span>{eventData?.venue?.name || "N/A"}</span>
                                        </div>

                                        <div className=" w-full flex justify-center">
                                            {/* Ticket Details */}
                                            <div className="grid grid-cols-3 w-full gap-4 mb-6">
                                                <div className="flex flex-col items-center">
                                                    <div className="flex items-center text-gray-500 mb-1">
                                                        <span className="text-lg mr-1">📅</span>
                                                        <span className="text-xs">Date</span>
                                                    </div>
                                                    <p className="text-sm font-medium text-center">{formattedDateTime(eventData?.eventDateTimes[0]?.date) || "N/A"}</p>
                                                </div>

                                                <div className="flex flex-col items-center border-l-2 border-r-2 border-dashed">
                                                    <div className="flex items-center text-gray-500 mb-1">
                                                        <span className="text-lg mr-1">👤</span>
                                                        <span className="text-xs">Name</span>
                                                    </div>
                                                    <p className="text-sm font-medium text-center">{registrationData?.name || 'N/A'}</p>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <div className="flex items-center text-gray-500 mb-1">
                                                        <span className="text-lg mr-1">🕞</span>
                                                        <span className="text-xs">Time</span>
                                                    </div>
                                                    <p className="text-sm font-medium text-center">{formattedTime(eventData?.eventDateTimes[0]?.startTime) || "N/A"}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {ticketsData[0]?.seatId && (
                                            <div className=" w-full flex justify-center">
                                                <div className="grid grid-cols-3 w-full gap-4 mb-6">
                                                    <div className="flex flex-col items-center">
                                                        <p className="text-xs text-gray-500">Zone</p>
                                                        <p className="text-sm font-medium text-center">{selectedTicket?.zone?.name || "-"}</p>
                                                    </div>

                                                    <div className="flex flex-col items-center border-l-2 border-r-2 border-dashed">
                                                        <p className="text-xs text-gray-500">Row</p>
                                                        <p className="text-sm font-medium text-center">{selectedTicket?.seat?.row || "-"}</p>
                                                    </div>

                                                    <div className="flex flex-col items-center">
                                                        <p className="text-xs text-gray-500">Seat</p>
                                                        <p className="text-sm font-medium text-center">{selectedTicket?.seat?.name || "-"}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="w-full flex items-center">
                                            <div className="flex-grow border-t-2 border-dashed mx-2"></div>
                                        </div>

                                        <div className="flex flex-col items-center pt-4 pb-3 w-full">
                                            <div className="w-full flex justify-center">
                                                <img
                                                    src={defaultcoverWhite}
                                                    alt="logo"
                                                    className="h-10"
                                                />
                                            </div>
                                            <p className="text-sm font-medium text-gray-600 mt-2">
                                                Scan your QR code at the entry gate.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Dots for multiple tickets */}
                                <div className="flex justify-center mt-4 gap-2">
                                    {ticketsData.map((_, index) => (
                                        <button
                                            key={index}
                                            className={`w-2 h-2 rounded-full ${selectedTicketIndex === index ? "bg-blue-500" : "bg-gray-300"}`}
                                            onClick={() => setSelectedTicketIndex(index)}
                                        />
                                    ))}
                                </div>
                            </div>

                        </div>

                        {/* Hidden section for generating PDFs */}
                        <div className="fixed top-0 left-0 w-0 h-0 overflow-hidden opacity-0 pointer-events-none" aria-hidden="true">
                            {ticketsData.map((ticket, index) => (
                                <div
                                    key={index}
                                    ref={el => (ticketRefs.current[index] = el)}
                                    style={{ width: "380px" }}
                                    className="rw-[380px] rounded-xl bg-white shadow-[0_-2px_10px_0_rgba(0,0,0,0.1)]"
                                >
                                    {/* Event Cover Image */}
                                    <div className="pt-4 px-4">
                                        <div className="h-48 rounded-lg overflow-hidden">
                                            <img
                                                src={eventData?.coverImage?.secure_url || defaultcover}
                                                alt="Event Cover"
                                                className="object-cover w-full h-full"
                                                crossOrigin="anonymous"
                                            />
                                        </div>
                                    </div>
                                    <div className="p-2 flex flex-col items-center">
                                        {/* QR Code Section */}
                                        <div className="bg-white -mt-20 rounded-lg mb-4 p-2 shadow-md">
                                            <div className="w-36 h-36 bg-white flex items-center justify-center border border-gray-200">
                                                <div className="text-center">
                                                    {ticket.qrCode && (
                                                        <img
                                                            src={ticket.qrCode}
                                                            alt="QR Code"
                                                            className="h-32 w-32"
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <p className="text-xs text-center mt-1 text-gray-500">{ticket.referenceId}</p>
                                        </div>

                                        {/* Event Title */}
                                        <h2 className="text-2xl font-bold text-center mb-1">{eventData?.name || "N/A"}</h2>
                                        <div className="flex items-center text-sm text-gray-600 mb-6">
                                            <span className="text-lg mr-1">📍</span>
                                            <span>{eventData?.venue?.name || "N/A"}</span>
                                        </div>

                                        {/* Ticket Info Grid */}
                                        <div className=" w-full flex justify-center">
                                            <div className="grid grid-cols-3 w-full gap-4 mb-6">
                                                <div className="flex flex-col items-center">
                                                    <div className="flex items-center text-gray-500 mb-1">
                                                        <span className="text-lg mr-1">📅</span>
                                                        <span className="text-xs">Date</span>
                                                    </div>
                                                    <p className="text-sm font-medium text-center">{formattedDateTime(eventData?.eventDateTimes[0]?.date) || "N/A"}</p>
                                                </div>

                                                <div className="flex flex-col items-center border-l-2 border-r-2 border-dashed">
                                                    <div className="flex items-center text-gray-500 mb-1">
                                                        <span className="text-lg mr-1">👤</span>
                                                        <span className="text-xs">Name</span>
                                                    </div>
                                                    <p className="text-sm font-medium text-center">{registrationData?.name || 'N/A'}</p>
                                                </div>

                                                <div className="flex flex-col items-center">
                                                    <div className="flex items-center text-gray-500 mb-1">
                                                        <span className="text-lg mr-1">🕞</span>
                                                        <span className="text-xs">Time</span>
                                                    </div>
                                                    <p className="text-sm font-medium text-center">{formattedTime(eventData?.eventDateTimes[0]?.startTime) || "N/A"}</p>
                                                </div>
                                            </div>
                                        </div>
                                        {ticket.seatId && (
                                            <div className=" w-full flex justify-center">
                                                <div className="grid grid-cols-3 w-full gap-4 mb-6">
                                                    <div className="flex flex-col items-center">
                                                        <p className="text-xs text-gray-500">Zone</p>
                                                        <p className="text-sm font-medium text-center">{ticket?.zone?.name || "-"}</p>
                                                    </div>

                                                    <div className="flex flex-col items-center border-l-2 border-r-2 border-dashed">
                                                        <p className="text-xs text-gray-500">Row</p>
                                                        <p className="text-sm font-medium text-center">{ticket?.seat?.row || "-"}</p>
                                                    </div>

                                                    <div className="flex flex-col items-center">
                                                        <p className="text-xs text-gray-500">Seat</p>
                                                        <p className="text-sm font-medium text-center">{ticket?.seat?.name || "-"}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="w-full flex items-center">
                                            <div className="flex-grow border-t-2 border-dashed mx-2"></div>
                                        </div>
                                        {/* Footer with Logo */}
                                        <div className="flex flex-col items-center pt-4 pb-3 w-full">
                                            <div className="w-full flex justify-center">
                                                <img
                                                    src={defaultcoverWhite}
                                                    alt="logo"
                                                    className="h-10"
                                                />
                                            </div>
                                            <p className="text-sm font-medium text-gray-600 mt-2">
                                                Scan your QR code at the entry gate.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <FooterTailwind />
        </TailwindLayout>
    );
}
